import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  MapPin, 
  Clock, 
  CreditCard, 
  Package, 
  QrCode,
  Share2,
  CheckCircle,
  AlertCircle,
  XCircle
} from 'lucide-react'
import { SecretOrder, SecretOrderItem, SecretEvent, updateOrder } from '../lib/supabase'
import { cancelTransfer } from '../lib/transfers'
import TransferOrderForm from '../components/transfers/TransferOrderForm'
import { useSingleOrderRealtime } from '../hooks/useOrderRealtime'
import OrderStatusIndicator, { OrderProgressIndicator } from '../components/orders/OrderStatusIndicator'
import QRCodeDisplay from '../components/orders/QRCodeDisplay'
import AnimatedQRCode from '../components/orders/AnimatedQRCode'
import AddToWalletButton from '../components/orders/AddToWalletButton'
import { useAuth } from '../contexts/AuthContext'
import { generateTOTPSecret, preGenerateCodes } from '../lib/totp'
import { supabase } from '../lib/supabase'
import { formatPrice } from '../lib/square'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../components/ui/EnterpriseToast'
import { cacheQRCodes, cacheOrderData } from '../lib/pwa'
import CancelledOrderLayout from '../components/orders/CancelledOrderLayout'

interface OrderWithDetails extends SecretOrder {
  secret_events: SecretEvent
  secret_order_items: Array<SecretOrderItem & {
    secret_menu_items: {
      name: string
      description?: string
      price: number
    }
  }>
}

const OrderDetailsPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  
  const [order, setOrder] = useState<OrderWithDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [showTransferForm, setShowTransferForm] = useState(false)
  const [totpSecret, setTotpSecret] = useState<string | null>(null)

  // Real-time order updates
  const { isConnected: isRealtimeConnected, lastUpdate } = useSingleOrderRealtime(
    orderId || '',
    {
      onStatusChange: (change) => {
        // Update local order state when status changes
        setOrder(prev => prev ? { ...prev, ...change.order } : null)
      },
      enableNotifications: true,
      enableToasts: true
    }
  )

  useEffect(() => {
    if (!orderId) {
      navigate('/orders')
      return
    }

    const loadOrder = async () => {
      try {
        setLoading(true)
        
        const { data, error } = await supabase
          .from('secret_orders')
          .select(`
            *,
            secret_events (
              id,
              title,
              location_name,
              address,
              event_date
            ),
            secret_order_items (
              id,
              quantity,
              unit_price,
              total_price,
              secret_menu_items (
                name,
                description,
                price
              )
            )
          `)
          .eq('id', orderId)
          .single()

        if (error) {
          console.error('Error loading order:', error)
          toast.custom((t) => (
            <EnterpriseToast
              id={t.id}
              type="error"
              title="Loading Failed"
              message="Could not load order details"
              visible={t.visible}
            />
          ))
          navigate('/orders')
          return
        }

        // Check if user has permission to view this order
        if (data.user_id !== user?.id) {
          toast.custom((t) => (
            <EnterpriseToast
              id={t.id}
              type="error"
              title="Order Not Found"
              message="This order could not be found"
              visible={t.visible}
            />
          ))
          navigate('/orders')
          return
        }

        setOrder(data as OrderWithDetails)

        // Generate or retrieve TOTP secret for QR code
        let secret: string | null = null
        if (data.qr_secret) {
          secret = data.qr_secret
          setTotpSecret(data.qr_secret)
        } else if (['confirmed', 'preparing', 'ready', 'completed'].includes(data.status)) {
          // Generate new secret for orders that need QR codes
          const newSecret = generateTOTPSecret()

          // Store secret in database
          const { error: updateError } = await supabase
            .from('secret_orders')
            .update({ qr_secret: newSecret })
            .eq('id', orderId)

          if (!updateError) {
            secret = newSecret
            setTotpSecret(newSecret)
          }
        }

        // Cache order and QR codes for offline access (PWA)
        if (secret) {
          try {
            // Cache order data
            await cacheOrderData(data)

            // Pre-generate and cache QR codes for offline use
            const qrCodes = await preGenerateCodes(orderId, secret)
            await cacheQRCodes(qrCodes)

            console.log('[PWA] Cached order and QR codes for offline access')
          } catch (error) {
            console.warn('[PWA] Failed to cache data:', error)
            // Don't block order loading if caching fails
          }
        }
      } catch (error) {
        console.error('Failed to load order:', error)
        toast.custom((t) => (
          <EnterpriseToast
            id={t.id}
            type="error"
            title="Loading Failed"
            message="Could not load order details. Please try again"
            visible={t.visible}
          />
        ))
      } finally {
        setLoading(false)
      }
    }

    if (user) {
      loadOrder()
    }
  }, [orderId, navigate, user])

  const handleCancelOrder = async () => {
    if (!order || order.status !== 'pending') return
    
    const confirmCancel = window.confirm(
      'Are you sure you want to cancel this order? This action cannot be undone.'
    )
    
    if (!confirmCancel) return

    setCancelLoading(true)
    try {
      await updateOrder(order.id, { status: 'cancelled' })
      setOrder(prev => prev ? { ...prev, status: 'cancelled' } : null)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="success"
          title="Order Cancelled"
          message="Your order has been cancelled"
          visible={t.visible}
        />
      ))
    } catch (error) {
      console.error('Failed to cancel order:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="error"
          title="Cancellation Failed"
          message="Could not cancel order. Please try again"
          visible={t.visible}
        />
      ))
    } finally {
      setCancelLoading(false)
    }
  }

  const handleCancelTransfer = async () => {
    if (!order?.transfer_token) return
    
    const confirmCancel = window.confirm(
      'Are you sure you want to cancel the transfer? The transfer link will become invalid.'
    )
    
    if (!confirmCancel) return

    try {
      const success = await cancelTransfer(order.id, user?.id || '')
      if (success) {
        setOrder(prev => prev ? {
          ...prev,
          transfer_token: null,
          transfer_expires_at: null
        } : null)
        toast.custom((t) => (
          <EnterpriseToast
            id={t.id}
            type="success"
            title="Transfer Cancelled"
            message="The order transfer has been cancelled"
            visible={t.visible}
          />
        ))
      } else {
        toast.custom((t) => (
          <EnterpriseToast
            id={t.id}
            type="error"
            title="Cancellation Failed"
            message="Could not cancel transfer"
            visible={t.visible}
          />
        ))
      }
    } catch (error) {
      console.error('Failed to cancel transfer:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="error"
          title="Cancellation Failed"
          message="Could not cancel transfer. Please try again"
          visible={t.visible}
        />
      ))
    }
  }

  const handleTransferSuccess = () => {
    setShowTransferForm(false)
    // Refresh order data
    window.location.reload()
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="text-yellow-500" size={20} />
      case 'confirmed':
        return <CheckCircle className="text-blue-500" size={20} />
      case 'preparing':
        return <Package className="text-orange-500" size={20} />
      case 'ready':
        return <CheckCircle className="text-green-500" size={20} />
      case 'completed':
        return <CheckCircle className="text-green-600" size={20} />
      case 'cancelled':
        return <XCircle className="text-red-500" size={20} />
      case 'transferred':
        return <Share2 className="text-purple-500" size={20} />
      default:
        return <AlertCircle className="text-muted-foreground" size={20} />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Payment Pending'
      case 'confirmed': return 'Order Confirmed'
      case 'preparing': return 'Being Prepared'
      case 'ready': return 'Ready for Pickup'
      case 'completed': return 'Completed'
      case 'cancelled': return 'Cancelled'
      case 'transferred': return 'Transferred'
      default: return 'Unknown Status'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'border-yellow-500 bg-card'
      case 'confirmed': return 'border-blue-500 bg-card'
      case 'preparing': return 'border-orange-500 bg-card'
      case 'ready': return 'border-green-500 bg-card'
      case 'completed': return 'border-green-600 bg-card'
      case 'cancelled': return 'border-red-500 bg-card'
      case 'transferred': return 'border-purple-500 bg-card'
      default: return 'border-border bg-card'
    }
  }

  const canCancelOrder = order && order.status === 'pending'
  const canTransferOrder = order && ['confirmed', 'preparing'].includes(order.status) && !order.transfer_token
  const hasActiveTransfer = order && order.transfer_token && order.transfer_expires_at && new Date(order.transfer_expires_at) > new Date()
  const showQRCode = order && ['ready', 'completed'].includes(order.status)

  if (loading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!order) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📋</div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Order Not Found</h2>
          <p className="text-secret-text-secondary mb-6">This order may no longer exist.</p>
          <Link to="/orders" className="secret-btn-primary">
            View All Orders
          </Link>
        </div>
      </div>
    )
  }

  // Use dedicated layout for cancelled orders
  if (order.status === 'cancelled') {
    return <CancelledOrderLayout order={order} />
  }

  return (
    <div className="min-h-screen-safe bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/orders')}
            className="flex items-center space-x-2 text-secret-text-secondary hover:text-foreground transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Orders</span>
          </button>
          
          <div className="text-right">
            <h1 className="text-2xl font-bold text-foreground">Order #{order.order_number}</h1>
            <p className="text-secret-text-secondary text-sm">
              Placed on {new Date(order.created_at).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Status */}
            <div className={`border-2 rounded-lg p-6 ${getStatusColor(order.status)}`}>
              <OrderStatusIndicator
                status={order.status}
                isRealtime={isRealtimeConnected}
                lastUpdate={lastUpdate}
                showLabel={true}
                size="lg"
                className="mb-4"
              />
              
              {/* Progress Indicator */}
              <div className="mt-6">
                <OrderProgressIndicator 
                  currentStatus={order.status}
                  className="max-w-md mx-auto"
                />
              </div>
              
              {order.status === 'pending' && (
                <p className="text-secret-text-secondary">
                  Your payment is being processed. You'll receive a confirmation once complete.
                </p>
              )}
              
              {order.status === 'confirmed' && (
                <p className="text-secret-text-secondary">
                  Your order has been confirmed! We'll notify you when it's being prepared.
                </p>
              )}
              
              {order.status === 'preparing' && (
                <p className="text-secret-text-secondary">
                  Your delicious SECRET order is being prepared with care.
                </p>
              )}
              
              {order.status === 'ready' && (
                <div className="space-y-2">
                  <p className="text-secret-text-secondary">
                    🎉 Your order is ready for pickup! Present your QR code to collect.
                  </p>
                  {order.pickup_time && (
                    <p className="text-secret-primary font-medium">
                      Pickup by: {new Date(order.pickup_time).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </p>
                  )}
                </div>
              )}
              
              {order.status === 'completed' && (
                <p className="text-secret-text-secondary">
                  Order completed! Thanks for being part of the SECRET experience.
                </p>
              )}
              
              {order.status === 'cancelled' && (
                <p className="text-secret-text-secondary">
                  This order has been cancelled. If you have questions, please contact support.
                </p>
              )}
            </div>

            {/* Order Items */}
            <div className="bg-card rounded-lg p-6 border border-border">
              <h3 className="text-xl font-bold text-foreground mb-4 flex items-center space-x-2">
                <Package size={20} />
                <span>Order Items</span>
              </h3>
              
              <div className="space-y-4">
                {order.secret_order_items.map((item) => (
                  <div key={item.id} className="flex justify-between items-start border-b border-secret-border pb-4">
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{item.secret_menu_items.name}</h4>
                      {item.secret_menu_items.description && (
                        <p className="text-sm text-secret-text-secondary mt-1">
                          {item.secret_menu_items.description}
                        </p>
                      )}
                      <p className="text-sm text-secret-text-secondary mt-1">
                        Quantity: {item.quantity} × {formatPrice(item.unit_price * 100)}
                      </p>
                    </div>
                    <div className="text-foreground font-medium ml-4">
                      {formatPrice(item.total_price * 100)}
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-between items-center pt-4 border-t border-secret-border">
                  <span className="text-lg font-bold text-foreground">Total</span>
                  <span className="text-xl font-bold text-secret-primary">
                    {formatPrice(order.total_amount * 100)}
                  </span>
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="bg-card rounded-lg p-6 border border-border">
              <h3 className="text-xl font-bold text-foreground mb-4 flex items-center space-x-2">
                <MapPin size={20} />
                <span>Event Details</span>
              </h3>
              
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-foreground">{order.secret_events.title}</h4>
                  <p className="text-secret-text-secondary">{order.secret_events.location_name}</p>
                  <p className="text-secret-text-secondary">{order.secret_events.address}</p>
                </div>
                <div className="flex items-center space-x-2 text-secret-text-secondary">
                  <Clock size={16} />
                  <span>
                    {new Date(order.secret_events.event_date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Animated QR Code with TOTP */}
            {totpSecret && showQRCode ? (
              <div className="bg-card rounded-lg p-6 border border-border">
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center space-x-2">
                  <QrCode size={20} className="text-secret-primary" />
                  <span>Pickup QR Code</span>
                </h3>
                <div className="flex justify-center">
                  <AnimatedQRCode
                    orderId={order.id}
                    orderSecret={totpSecret}
                    size={280}
                    showTimer={true}
                  />
                </div>
                <p className="text-xs text-secret-text-tertiary text-center mt-4">
                  Show this code at pickup. Code rotates every 15 seconds for security.
                </p>
              </div>
            ) : !showQRCode && order.status === 'confirmed' ? (
              <div className="bg-card rounded-lg p-6 text-center border border-border">
                <QrCode size={48} className="text-secret-text-tertiary mx-auto mb-3" />
                <h3 className="font-medium text-foreground mb-2">QR Code Coming Soon</h3>
                <p className="text-sm text-secret-text-secondary">
                  Your QR code will be available when your order is ready for pickup
                </p>
              </div>
            ) : null}

            {/* Add to Wallet */}
            {totpSecret && showQRCode && (
              <div className="bg-card rounded-lg p-6 border border-border">
                <AddToWalletButton
                  passData={{
                    orderId: order.id,
                    orderNumber: order.order_number,
                    eventTitle: order.secret_events.title,
                    eventLocation: order.secret_events.location_name,
                    eventDate: order.secret_events.event_date,
                    qrCodeData: `${order.id}|${totpSecret}`,
                    customerName: `${user?.email || 'Guest'}`,
                    totalAmount: order.total_amount
                  }}
                />
              </div>
            )}

            {/* Payment Info */}
            <div className="bg-card rounded-lg p-6 border border-border">
              <h3 className="font-bold text-foreground mb-4 flex items-center space-x-2">
                <CreditCard size={20} />
                <span>Payment</span>
              </h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-secret-text-secondary">Payment Method:</span>
                  <span className="text-foreground">Square</span>
                </div>
                {order.square_payment_id && (
                  <div className="flex justify-between">
                    <span className="text-secret-text-secondary">Payment ID:</span>
                    <span className="text-foreground text-xs">
                      {order.square_payment_id.substring(0, 12)}...
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-secret-text-secondary">Status:</span>
                  <span className={`${
                    order.status === 'pending' ? 'text-yellow-500' : 'text-green-500'
                  }`}>
                    {order.status === 'pending' ? 'Processing' : 'Paid'}
                  </span>
                </div>
              </div>
            </div>

            {/* Transfer Status */}
            {hasActiveTransfer && (
              <div className="bg-purple-600/20 border border-purple-600 rounded-lg p-4">
                <h3 className="font-bold text-purple-400 mb-2 flex items-center space-x-2">
                  <Share2 size={16} />
                  <span>Transfer Active</span>
                </h3>
                <p className="text-purple-200 text-sm mb-3">
                  Your order is being transferred. The link expires on{' '}
                  {order.transfer_expires_at && new Date(order.transfer_expires_at).toLocaleString()}
                </p>
                <button
                  onClick={handleCancelTransfer}
                  className="text-sm text-purple-400 hover:text-purple-300 underline"
                >
                  Cancel Transfer
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              {canTransferOrder && (
                <button
                  onClick={() => setShowTransferForm(true)}
                  className="secret-btn-secondary w-full flex items-center justify-center space-x-2"
                >
                  <Share2 size={16} />
                  <span>Transfer Order</span>
                </button>
              )}
              
              {canCancelOrder && (
                <button
                  onClick={handleCancelOrder}
                  disabled={cancelLoading}
                  className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  {cancelLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <XCircle size={16} />
                      <span>Cancel Order</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Support */}
            <div className="bg-card rounded-lg p-6 text-center border border-border">
              <h3 className="font-bold text-foreground mb-2">Need Help?</h3>
              <p className="text-secret-text-secondary text-sm mb-4">
                Questions about your order? We're here to help!
              </p>
              <button className="secret-btn-secondary text-sm">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Transfer Form Modal */}
      {showTransferForm && order && user && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-secret-bg-primary rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <TransferOrderForm
              order={order}
              user={user}
              onSuccess={handleTransferSuccess}
              onCancel={() => setShowTransferForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default OrderDetailsPage